import type { Dispatch, SetStateAction } from 'react';

import type { DataTableV2ColumnVisibilityState } from '../../../features/ColumnVisibility/ColumnVisibility.js';
import type { Column } from '../../../hooks/useTable/types.js';

/**
 * Generates an object with the visibility of each column.
 * @param columns - The columns in the table.
 * @param columnVisibility - The visibility of the table columns.
 * @returns an object that contains the column ids as keys with their visibility as values.
 * @internal
 */
export function generateColumnVisibilityObject(
  columns: Column<unknown, unknown>[],
  columnVisibility?: Record<string, boolean>,
) {
  return Object.fromEntries(
    columns.map((column) => [
      column.id,
      columnVisibility
        ? columnVisibility[column.id] ?? true
        : column.getIsVisible(),
    ]),
  );
}

/**
 * Updates the local column visibility of multiple columns used when clicking show/hide all or parent column checkboxes.
 * @param columns - The columns in the table.
 * @param currentVisibilityState - The current column visibility state in the table.
 * @param localColumnVisibility - The current column visibility state in the settings.
 * @param setLocalColumnVisibility - Setter to update the column visibility state in the settings.
 * @internal
 */
export function updateMultipleLocalColumnVisibility(
  columns: Column<unknown, unknown>[],
  currentVisibilityState: boolean | 'indeterminate',
  localColumnVisibility: Record<string, boolean>,
  setLocalColumnVisibility: Dispatch<SetStateAction<Record<string, boolean>>>,
) {
  const newVisibilityState = currentVisibilityState !== true;
  setLocalColumnVisibility(
    columns.reduce((visibilityState, column) => {
      return {
        ...visibilityState,
        [column.id]: column.getCanHide() ? newVisibilityState : true,
      };
    }, localColumnVisibility),
  );
}

/**
 * Gets the combined checked value for a parent visibiliy checkbox
 * @param columnVisibility - The visibility of the table columns.
 * @returns `true` if all columns to consider a visible, `false` all columns are hidden and `indeterminate` if some are visible and some hidden
 * @internal
 */
export function getCombinedVisibilityCheckboxState(
  columnVisibility: Record<string, boolean>,
) {
  const visibilityStates = Object.values(columnVisibility);
  if (visibilityStates.every((value) => value === true)) {
    return true;
  }
  if (visibilityStates.every((value) => value === false)) {
    return false;
  }
  return 'indeterminate';
}

/**
 * Determines whether the visibility of one or multiple columns has changed.
 * @param columnVisibilityToReset - The column visibility state that is used to reset.
 * @param localColumnVisibility - The current column visibility state in the settings.
 * @returns `true` if the visibility has changed for at least one column, `false` otherwise.
 * @internal
 */
export function getHasVisibilityChanged(
  columnVisibilityToReset: DataTableV2ColumnVisibilityState | undefined,
  localColumnVisibility: Record<string, boolean>,
) {
  if (!columnVisibilityToReset) {
    return false;
  }

  return Object.keys(localColumnVisibility).some((key) => {
    // If the key is not present in columnVisibilityToReset, consider it as visible
    if (!(key in columnVisibilityToReset)) {
      return localColumnVisibility[key] !== true;
    }
    return localColumnVisibility[key] !== columnVisibilityToReset[key];
  });
}

/**
 * Determines whether the order of one or multiple columns has changed.
 * @param columnOrderToReset - The column order state that is used to reset.
 * @param localColumnOrder - The current column order state in the settings..
 * @returns `true` if the order has changed for at least one column, `false` otherwise.
 * @internal
 */
export function getHasColumnOrderChanged(
  columnOrderToReset: string[] | undefined,
  localColumnOrder: string[],
) {
  if (!columnOrderToReset) {
    return false;
  }
  if (columnOrderToReset.length !== localColumnOrder.length) {
    return true;
  }
  return columnOrderToReset.some(
    (value, index) => value !== localColumnOrder[index],
  );
}
